import { useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import type { ResourceLookupRegistry } from '@/shared/components/ui/resource-lookup-context'
import type { ResourceOption } from '@/shared/components/ui/resource-search-input'
import api from '@/consumer/lib/api'

/**
 * Consumer-side resource lookup providers, keyed by provider name (matching the
 * `configuration_data.lookup_provider` on an `ENTITY_SEARCH` field). Mirrors the
 * admin `useResourceLookupProviders`, but scoped to the consumer surface and its
 * tenant-scoped endpoints.
 *
 * `partnership` = the consumer's tenant dealer / retailer list, powering the
 * registration Dealer / Retailer picker.
 *
 * Routed through `queryClient.fetchQuery` with a term-inclusive
 * `['resource-search','partnership',term]` key (SEARCH policy, registered in
 * `consumer/lib/query-defaults.ts`) so re-opening the picker or retyping a term
 * repaints from the in-memory cache instead of re-hitting `/dealers/` on every
 * keystroke - matching the admin `partnership` provider.
 */
export function useConsumerResourceLookupProviders(): ResourceLookupRegistry {
  const queryClient = useQueryClient()
  return useMemo<ResourceLookupRegistry>(
    () => ({
      partnership: {
        search: (term: string): Promise<ResourceOption[]> =>
          queryClient.fetchQuery({
            queryKey: ['resource-search', 'partnership', term],
            queryFn: async () => {
              const res = await api.get('/dealers/', {
                params: { search: term },
              })
              const results = (res.data?.results ?? []) as {
                uuid: string
                name: string
              }[]
              return results.map((r) => ({ uuid: r.uuid, name: r.name }))
            },
          }),
      },
    }),
    [queryClient],
  )
}
